import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SupportPerformanceService } from '../lib/services/analytics/supportPerformanceService';
import type { HumanHandlingTimeData } from '../lib/services/analytics/supportPerformanceService';
import type {
  SupportMetricsData,
  QuestionCountData,
  EngagementTimeData,
  DateRange,
  SupportPerformanceFilters,
} from '../lib/services/analytics/supportPerformanceService';
import { useAuthStore } from './authStore';

const padTwo = (value: number) => String(value).padStart(2, '0');

const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const getLocalHourKey = (date: Date): string =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;

interface MetricData {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: number;
  timeRange?: string;
  showViewReport?: boolean;
  chartData?: {
    labels: string[];
    data: number[];
    recordCounts?: number[]; // For engagement time data
  };
  chartColor?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  rawValue?: number; // Raw value in seconds for comparison charts
  /**
   * 人工从接入到收工的平均耗时（秒）。样本为 0 时**保持 undefined** —— 对照柱子会被省略。
   * 那根柱子此前是 ComparisonBarChart 里硬编码的 6 分钟常量。
   */
  humanValue?: number;
}

interface SupportPerformanceState {
  // Legacy metrics for backward compatibility
  metrics: MetricData[];

  // New real API data
  avgTimeFirstResponse: SupportMetricsData | null;
  questionCount: QuestionCountData | null;
  engagementTime: EngagementTimeData | null;
  humanHandlingTime: HumanHandlingTimeData | null;

  // Today's conversations count (always shows today's data regardless of selected time range)
  todayConversations: number | null;

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Date filtering
  dateRange: DateRange;
  selectedPeriod: string;
  selectedTimeRange: string;

  // Individual card time ranges
  cardTimeRanges: Record<string, string>;

  // Card-specific data
  cardMetrics: Record<string, MetricData>;
  cardLoadingStates: Record<string, boolean>;

  // Legacy methods (for backward compatibility)
  setMetrics: (metrics: MetricData[]) => void;
  setLoading: (loading: boolean) => void;

  // New API methods
  fetchSupportMetrics: (dateRange?: DateRange, timeRange?: string) => Promise<void>;
  fetchMetricForCard: (
    cardId: string,
    metricType: string,
    timeRange: string,
    customRange?: DateRange
  ) => Promise<void>;
  setDateRange: (range: DateRange) => void;
  setSelectedPeriod: (period: string) => void;
  setSelectedTimeRange: (timeRange: string) => void;
  setCardTimeRange: (cardId: string, timeRange: string) => void;
  setError: (error: string | null) => void;

  // Data transformation methods
  transformToLegacyMetrics: () => void;

  // Reset store state (for user switch)
  reset: () => void;
}

// Default date range (last 7 days)
const getDefaultDateRange = (): DateRange => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  return {
    startDate: formatLocalDate(sevenDaysAgo),
    endDate: formatLocalDate(today),
  };
};

// Generate date range for filling missing dates
const generateDateRange = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  while (start <= end) {
    dates.push(formatLocalDate(start));
    start.setDate(start.getDate() + 1);
  }

  return dates;
};

// Generate labels based on date range for empty state
const generateEmptyStateLabels = (dateRange?: { startDate: string; endDate: string }) => {
  if (!dateRange) {
    // Default to month labels if no date range
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  }

  const start = parseLocalDate(dateRange.startDate);
  const end = parseLocalDate(dateRange.endDate);
  const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  const labels: string[] = [];

  if (diffDays < 1) {
    // Same day - show 24 hours
    for (let i = 0; i < 24; i += 4) {
      labels.push(`${i}:00`);
    }
  } else if (diffDays <= 7) {
    // 7 days - show each day
    const current = new Date(start);
    while (current <= end) {
      labels.push(current.toLocaleDateString('en-US', { weekday: 'short' }));
      current.setDate(current.getDate() + 1);
    }
  } else if (diffDays <= 30) {
    // 30 days - show dates
    const current = new Date(start);
    const step = Math.ceil(diffDays / 10); // Show ~10 labels
    while (current <= end) {
      labels.push(current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      current.setDate(current.getDate() + step);
    }
  } else if (diffDays <= 90) {
    // 3 months - show weeks
    const current = new Date(start);
    while (current <= end) {
      labels.push(current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      current.setDate(current.getDate() + 7);
    }
  } else {
    // 12 months - show months
    const current = new Date(start);
    while (current <= end) {
      labels.push(current.toLocaleDateString('en-US', { month: 'short' }));
      current.setMonth(current.getMonth() + 1);
    }
  }

  return labels.length > 0
    ? labels
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
};

// Transform time series data to chart format with missing date filling
const transformTimeSeriesData = (
  timeSeriesData: { date: string; value: number; formattedDate: string; recordCount?: number }[],
  dateRange?: { startDate: string; endDate: string },
  isCustomRange?: boolean
) => {
  // When no data, return empty state with labels based on date range
  if (!timeSeriesData || timeSeriesData.length === 0) {
    const labels = generateEmptyStateLabels(dateRange);
    return {
      labels,
      data: labels.map(() => 0),
      recordCounts: labels.map(() => 0),
    };
  }

  // If dateRange is provided, fill missing dates with 0 values
  if (dateRange) {
    const start = parseLocalDate(dateRange.startDate);
    const end = parseLocalDate(dateRange.endDate);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    // For custom range, handle differently based on date range
    if (isCustomRange) {
      // For same day custom range, use hourly data (diffDays < 1 means same day)
      if (diffDays < 1) {
        const hasHourlyData = timeSeriesData.some(point => point.date.includes('T'));

        if (hasHourlyData) {
          // Generate all hours for the date range (local time)
          const allHours: { hourKey: string; dateObj: Date }[] = [];
          const currentHour = new Date(start);
          currentHour.setHours(0, 0, 0, 0);

          const endHour = new Date(end);
          endHour.setHours(23, 0, 0, 0);

          while (currentHour <= endHour) {
            allHours.push({
              hourKey: getLocalHourKey(currentHour),
              dateObj: new Date(currentHour),
            });
            currentHour.setHours(currentHour.getHours() + 1);
          }

          // Create map of existing data by hour (local time)
          const dataMap = new Map<string, { value: number; recordCount?: number }>();
          timeSeriesData.forEach(point => {
            const pointDate = new Date(point.date);
            const hourKey = getLocalHourKey(pointDate);
            dataMap.set(hourKey, {
              value: point.value,
              recordCount: point.recordCount,
            });
          });

          // Fill missing hours with 0 values
          const filledData = allHours.map(({ dateObj }) => {
            const hourKey = getLocalHourKey(dateObj);
            const existing = dataMap.get(hourKey);

            return {
              value: existing ? existing.value : 0,
              formattedDate: dateObj.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }),
              recordCount: existing?.recordCount || 0,
            };
          });

          return {
            labels: filledData.map(point => point.formattedDate),
            data: filledData.map(point => point.value),
            recordCounts: filledData.map(point => point.recordCount || 0),
          };
        }
      }

      if (diffDays > 30) {
        // Custom range > 30 days: use same logic as 3 months (aggregate by week, show dates)
        const weeklyData = new Map<string, { total: number; count: number; recordCount: number }>();

        timeSeriesData.forEach(point => {
          const dateObj = point.date.includes('T') ? new Date(point.date) : parseLocalDate(point.date);
          const weekStart = new Date(dateObj);
          weekStart.setDate(dateObj.getDate() - dateObj.getDay());
          const weekKey = formatLocalDate(weekStart);

          if (!weeklyData.has(weekKey)) {
            weeklyData.set(weekKey, { total: 0, count: 0, recordCount: 0 });
          }
          const existing = weeklyData.get(weekKey)!;
          existing.total += point.value;
          existing.count += 1;
          existing.recordCount += point.recordCount || 0;
        });

        const weeks: string[] = [];
        const current = new Date(start);
        current.setDate(current.getDate() - current.getDay());

        while (current <= end) {
          weeks.push(formatLocalDate(current));
          current.setDate(current.getDate() + 7);
        }

        const labels = weeks.map(weekKey => {
          const dateObj = parseLocalDate(weekKey);
          return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        const data = weeks.map(weekKey => {
          const weekData = weeklyData.get(weekKey);
          if (weekData && weekData.count > 0) {
            return Math.round(weekData.total / weekData.count);
          }
          return 0;
        });

        const recordCounts = weeks.map(weekKey => {
          const weekData = weeklyData.get(weekKey);
          return weekData ? weekData.recordCount : 0;
        });

        return { labels, data, recordCounts };
      } else {
        // Custom range ≤ 30 days: show daily data (skip some if needed)
        const allDates = generateDateRange(dateRange.startDate, dateRange.endDate);
        const dataMap = new Map<
          string,
          { value: number; formattedDate: string; recordCount?: number }
        >();

        timeSeriesData.forEach(point => {
          dataMap.set(point.date, {
            value: point.value,
            formattedDate: point.formattedDate,
            recordCount: point.recordCount,
          });
        });

        const filledData = allDates.map(date => {
          const existing = dataMap.get(date);
          if (existing) {
            return {
              date,
              value: existing.value,
              formattedDate: existing.formattedDate,
              recordCount: existing.recordCount,
            };
          } else {
            const dateObj = parseLocalDate(date);
            return {
              date,
              value: 0,
              formattedDate: dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              }),
              recordCount: 0,
            };
          }
        });

        return {
          labels: filledData.map(point => point.formattedDate),
          data: filledData.map(point => point.value),
          recordCounts: filledData.map(point => point.recordCount || 0),
        };
      }
    }

    // For same day (hourly data), fill missing hours (diffDays < 1 means same day)
    if (diffDays < 1) {
      // Check if we have hourly data (date contains time info)
      const hasHourlyData = timeSeriesData.some(point => point.date.includes('T'));

      if (hasHourlyData) {
          // Generate all hours for the date range (local time)
          const allHours: { hourKey: string; dateObj: Date }[] = [];
          const currentHour = new Date(start);
          currentHour.setHours(0, 0, 0, 0);

          const endHour = new Date(end);
          endHour.setHours(23, 0, 0, 0);

          while (currentHour <= endHour) {
            allHours.push({
              hourKey: getLocalHourKey(currentHour),
              dateObj: new Date(currentHour),
            });
            currentHour.setHours(currentHour.getHours() + 1);
          }

          // Create map of existing data by hour (local time)
          const dataMap = new Map<string, { value: number; recordCount?: number }>();
          timeSeriesData.forEach(point => {
            const pointDate = new Date(point.date);
            const hourKey = getLocalHourKey(pointDate);
            dataMap.set(hourKey, {
              value: point.value,
              recordCount: point.recordCount,
            });
          });

          // Fill missing hours with 0 values
          const filledData = allHours.map(({ dateObj }) => {
            const hourKey = getLocalHourKey(dateObj);
            const existing = dataMap.get(hourKey);

          return {
            value: existing ? existing.value : 0,
            formattedDate: dateObj.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }),
            recordCount: existing?.recordCount || 0,
          };
        });

        return {
          labels: filledData.map(point => point.formattedDate),
          data: filledData.map(point => point.value),
          recordCounts: filledData.map(point => point.recordCount || 0),
        };
      }
    }

    // For 3 months (30-90 days), aggregate data by week
    if (diffDays > 30 && diffDays <= 90) {
      const weeklyData = new Map<string, { total: number; count: number; recordCount: number }>();

      // Aggregate data by week
      timeSeriesData.forEach(point => {
        const dateObj = point.date.includes('T') ? new Date(point.date) : parseLocalDate(point.date);
        // Get week start date (Sunday)
        const weekStart = new Date(dateObj);
        weekStart.setDate(dateObj.getDate() - dateObj.getDay());
        const weekKey = formatLocalDate(weekStart);

        if (!weeklyData.has(weekKey)) {
          weeklyData.set(weekKey, { total: 0, count: 0, recordCount: 0 });
        }
        const existing = weeklyData.get(weekKey)!;
        existing.total += point.value;
        existing.count += 1;
        existing.recordCount += point.recordCount || 0;
      });

      // Generate all weeks in range
      const weeks: string[] = [];
      const current = new Date(start);
      current.setDate(current.getDate() - current.getDay()); // Start from Sunday

      while (current <= end) {
        weeks.push(formatLocalDate(current));
        current.setDate(current.getDate() + 7);
      }

      // Build result with weekly averages
      const labels = weeks.map(weekKey => {
        const dateObj = parseLocalDate(weekKey);
        return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });

      const data = weeks.map(weekKey => {
        const weekData = weeklyData.get(weekKey);
        if (weekData && weekData.count > 0) {
          return Math.round(weekData.total / weekData.count); // Average
        }
        return 0;
      });

      const recordCounts = weeks.map(weekKey => {
        const weekData = weeklyData.get(weekKey);
        return weekData ? weekData.recordCount : 0;
      });

      return { labels, data, recordCounts };
    }

    // For 12 months (> 90 days), aggregate data by month
    if (diffDays > 90) {
      const monthlyData = new Map<string, { total: number; count: number; recordCount: number }>();

      // Aggregate data by month
      timeSeriesData.forEach(point => {
        const dateObj = point.date.includes('T') ? new Date(point.date) : parseLocalDate(point.date);
        const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;

        if (!monthlyData.has(monthKey)) {
          monthlyData.set(monthKey, { total: 0, count: 0, recordCount: 0 });
        }
        const existing = monthlyData.get(monthKey)!;
        existing.total += point.value;
        existing.count += 1;
        existing.recordCount += point.recordCount || 0;
      });

      // Generate all months in range
      const months: string[] = [];
      const current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

      while (current <= endMonth) {
        months.push(`${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`);
        current.setMonth(current.getMonth() + 1);
      }

      // Build result with monthly averages
      const labels = months.map(monthKey => {
        const [year, month] = monthKey.split('-');
        const dateObj = new Date(parseInt(year), parseInt(month) - 1, 1);
        return dateObj.toLocaleDateString('en-US', { month: 'short' });
      });

      const data = months.map(monthKey => {
        const monthData = monthlyData.get(monthKey);
        if (monthData && monthData.count > 0) {
          return Math.round(monthData.total / monthData.count); // Average
        }
        return 0;
      });

      const recordCounts = months.map(monthKey => {
        const monthData = monthlyData.get(monthKey);
        return monthData ? monthData.recordCount : 0;
      });

      return { labels, data, recordCounts };
    }

    // For shorter ranges, use daily data
    const allDates = generateDateRange(dateRange.startDate, dateRange.endDate);
    const dataMap = new Map<
      string,
      { value: number; formattedDate: string; recordCount?: number }
    >();

    // Create map of existing data
    timeSeriesData.forEach(point => {
      dataMap.set(point.date, {
        value: point.value,
        formattedDate: point.formattedDate,
        recordCount: point.recordCount,
      });
    });

    // Fill missing dates with 0 values
    const filledData = allDates.map(date => {
      const existing = dataMap.get(date);
      if (existing) {
        return {
          date,
          value: existing.value,
          formattedDate: existing.formattedDate,
          recordCount: existing.recordCount,
        };
      } else {
        // Create formatted date for missing dates
        const dateObj = parseLocalDate(date);
        return {
          date,
          value: 0,
          formattedDate: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          recordCount: 0,
        };
      }
    });

    return {
      labels: filledData.map(point => point.formattedDate),
      data: filledData.map(point => point.value),
      recordCounts: filledData.map(point => point.recordCount || 0),
    };
  }

  // Original behavior when no dateRange is provided
  return {
    labels: timeSeriesData.map(point => point.formattedDate),
    data: timeSeriesData.map(point => point.value),
    recordCounts: timeSeriesData.map(point => point.recordCount || 0),
  };
};

export const useSupportPerformanceStore = create<SupportPerformanceState>()(
  persist(
    (set, get) => ({
      // Legacy metrics (empty initially, populated by real data)
      metrics: [],

      // Real API data
      avgTimeFirstResponse: null,
      humanHandlingTime: null,
      questionCount: null,
      engagementTime: null,
      todayConversations: null,

      // State
      isLoading: false,
      error: null,
      dateRange: getDefaultDateRange(),
      selectedPeriod: 'last7Days',
      selectedTimeRange: '12months',
      cardTimeRanges: {},
      cardMetrics: {},
      cardLoadingStates: {},

      // Legacy methods
      setMetrics: metrics => set({ metrics }),
      setLoading: loading => set({ isLoading: loading }),

      // New API methods
      fetchSupportMetrics: async (dateRange, timeRange) => {
        const state = get();

        // Prevent duplicate requests if already loading
        if (state.isLoading) {
          return;
        }

        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        // Calculate date range based on timeRange parameter if provided
        let currentDateRange = dateRange || state.dateRange;
        const currentTimeRange = timeRange || state.selectedTimeRange;

        // If timeRange is provided but not dateRange, calculate the date range
        if (timeRange && !dateRange) {
          const timeRangeMap: Record<string, number> = {
            '24hours': 1,
            '7days': 7,
            '30days': 30,
            '3months': 90,
            '12months': 365,
          };

          const days = timeRangeMap[timeRange];
          if (days) {
            const endDate = new Date();
            // Special case for 24hours: use today's date for both start and end
            // to ensure we only get today's data
            if (timeRange === '24hours') {
              currentDateRange = {
                startDate: formatLocalDate(endDate),
                endDate: formatLocalDate(endDate),
              };
            } else {
              const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
              currentDateRange = {
                startDate: formatLocalDate(startDate),
                endDate: formatLocalDate(endDate),
              };
            }
          }
        }

        set({ isLoading: true, error: null });

        try {
          const filters: SupportPerformanceFilters = {
            ...currentDateRange,
            userId: user.id,
            granularity: SupportPerformanceService.getOptimalGranularity(
              currentDateRange.startDate,
              currentDateRange.endDate
            ),
          };

          console.log('fetchSupportMetrics filters:', {
            dateRange: currentDateRange,
            granularity: filters.granularity,
          });

          // Get today's date for fetching today's conversations count
          const today = formatLocalDate(new Date());
          const todayFilters: SupportPerformanceFilters = {
            startDate: today,
            endDate: today,
            userId: user.id,
            granularity: 'day',
          };

          // Fetch all metrics and today's conversations in parallel
          const [data, todayData, humanTime] = await Promise.all([
            SupportPerformanceService.getAllSupportMetrics(filters),
            SupportPerformanceService.getQuestionCountMetrics(todayFilters),
            SupportPerformanceService.getHumanHandlingTime(filters),
          ]);

          set({
            avgTimeFirstResponse: data.avgTimeFirstResponse,
            questionCount: data.questionCount,
            engagementTime: data.engagementTime,
            humanHandlingTime: humanTime,
            todayConversations: todayData.totalConversations,
            selectedTimeRange: currentTimeRange,
            isLoading: false,
            error: null,
            // Clear card-specific data so cards use global data
            cardMetrics: {},
            cardTimeRanges: {},
          });

          // Transform to legacy format for backward compatibility
          get().transformToLegacyMetrics();
        } catch (error) {
          console.error('Failed to fetch support metrics:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load support metrics',
          });
        }
      },

      setDateRange: range => {
        const currentRange = get().dateRange;
        // Only fetch if range actually changed
        if (range.startDate !== currentRange.startDate || range.endDate !== currentRange.endDate) {
          set({ dateRange: range });
          get().fetchSupportMetrics(range);
        }
      },

      setSelectedPeriod: period => {
        const currentPeriod = get().selectedPeriod;
        // Only fetch if period actually changed
        if (period !== currentPeriod) {
          // For custom range, just update the period without changing dateRange
          if (period === 'custom') {
            set({ selectedPeriod: period });
            return;
          }
          const presets = SupportPerformanceService.getDateRangePresets();
          const dateRange = presets[period];
          if (dateRange) {
            set({ selectedPeriod: period, dateRange });
            get().fetchSupportMetrics(dateRange);
          }
        }
      },

      setSelectedTimeRange: timeRange => {
        set({ selectedTimeRange: timeRange });
        // Auto-fetch when time range changes
        get().fetchSupportMetrics(get().dateRange, timeRange);
      },

      fetchMetricForCard: async (cardId, metricType, timeRange, customRange) => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          return;
        }

        // Set loading state for this card
        set(state => ({
          cardLoadingStates: {
            ...state.cardLoadingStates,
            [cardId]: true,
          },
        }));

        try {
          let dateRange: DateRange;

          // Use custom range if provided, otherwise calculate from timeRange
          if (timeRange === 'custom' && customRange) {
            dateRange = customRange;
          } else {
            // Calculate date range based on timeRange
            const timeRangeMap: Record<string, number> = {
              '24hours': 1,
              '7days': 7,
              '30days': 30,
              '3months': 90,
              '12months': 365,
            };

            const days = timeRangeMap[timeRange] || 7;
            const endDate = new Date();
            // Special case for 24hours: use today's date for both start and end
            if (timeRange === '24hours') {
              dateRange = {
                startDate: formatLocalDate(endDate),
                endDate: formatLocalDate(endDate),
              };
            } else {
              const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
              dateRange = {
                startDate: formatLocalDate(startDate),
                endDate: formatLocalDate(endDate),
              };
            }
          }

          const filters: SupportPerformanceFilters = {
            ...dateRange,
            userId: user.id,
            granularity: SupportPerformanceService.getOptimalGranularity(
              dateRange.startDate,
              dateRange.endDate
            ),
          };

          console.log('fetchMetricForCard filters:', {
            cardId,
            metricType,
            timeRange,
            dateRange,
            granularity: filters.granularity,
          });

          // Fetch specific metric based on type
          let metricData: MetricData | null = null;
          const isCustomRange = timeRange === 'custom';

          if (metricType === 'avgTimeFirstResponse') {
            // 人工耗时必须**用这张卡自己的 filters** 取。这个分支一旦跑过，
            // cardMetrics[cardId] 就会盖住全局 metric —— 不在这里取的话 humanValue 变成
            // undefined、对照柱子当场消失；用全局 filters 取则两根柱子覆盖不同的时间范围。
            const [data, humanTime] = await Promise.all([
              SupportPerformanceService.getAvgTimeFirstResponse(filters),
              SupportPerformanceService.getHumanHandlingTime(filters),
            ]);
            metricData = {
              title: 'Average resolution time',
              value: SupportPerformanceService.formatTimeValue(data.avgTimeFirstResponse),
              subtitle: 'Average resolution time',
              change: data.trend || 0,
              chartData: transformTimeSeriesData(data.timeSeriesData, dateRange, isCustomRange),
              chartColor: '#6B33FF',
              yAxisLabel: 'Response Time',
              xAxisLabel: 'Time Period',
              rawValue: data.avgTimeFirstResponse, // Raw value in seconds for comparison chart
              // 样本为 0 时留 undefined，让对照柱子消失而不是显示 0 秒
              humanValue: humanTime.recordCount > 0 ? humanTime.avgSeconds : undefined,
            };
          } else if (metricType === 'resolvedConversations') {
            const data = await SupportPerformanceService.getQuestionCountMetrics(filters);
            metricData = {
              title: 'Resolved Conversations',
              value: data.totalResolvedConversations.toLocaleString(),
              subtitle: 'Total resolved conversations',
              change: data.resolvedTrend || 0,
              chartData: transformTimeSeriesData(
                data.resolvedConversationsData,
                dateRange,
                isCustomRange
              ),
              chartColor: '#00BC2F',
              yAxisLabel: 'Conversations',
              xAxisLabel: 'Time Period',
            };
          } else if (metricType === 'resolutionRate') {
            const data = await SupportPerformanceService.getQuestionCountMetrics(filters);
            metricData = {
              title: 'Resolution Rate',
              value: `${data.averageResolutionRate.toFixed(1)}%`,
              subtitle: 'Percent of resolved conversations',
              change: data.rateTrend || 0,
              chartData: transformTimeSeriesData(data.resolutionRateData, dateRange, isCustomRange),
              chartColor: '#FBD300',
              yAxisLabel: 'Resolution Rate (%)',
              xAxisLabel: 'Time Period',
            };
          } else if (metricType === 'engagementTime') {
            const data = await SupportPerformanceService.getEngagementTimeMetrics(filters);
            metricData = {
              title: 'Average engagement time',
              value: SupportPerformanceService.formatTimeValue(data.averageEngagementTime),
              subtitle:
                'Total duration a user actively interacts with the chatbot including typing, clicking, or reading responses.',
              change: data.trend || 0,
              chartData: transformTimeSeriesData(data.timeSeriesData, dateRange, isCustomRange),
              chartColor: '#00BC2F',
              yAxisLabel: 'Avg engagement time',
              xAxisLabel: 'Time Period',
            };
          } else if (metricType === 'aiGeneratedOrders') {
            // AI Generated Orders - no API yet, always show empty
            metricData = {
              title: 'AI Generated Orders',
              value: '0',
              subtitle: 'Percent of resolved conversations',
              chartData: {
                labels: generateEmptyStateLabels(dateRange),
                data: generateEmptyStateLabels(dateRange).map(() => 0),
                recordCounts: [],
              },
              chartColor: '#FF41B3',
              yAxisLabel: 'Orders',
              xAxisLabel: 'Time Period',
            };
          } else if (metricType === 'handledByHumans') {
            // Handled by Humans - no API yet, always show empty
            metricData = {
              title: 'Handled by Humans',
              value: '',
              showViewReport: true,
              chartData: {
                labels: generateEmptyStateLabels(dateRange),
                data: generateEmptyStateLabels(dateRange).map(() => 0),
                recordCounts: [],
              },
              chartColor: '#FF5E57',
              yAxisLabel: 'Sessions',
              xAxisLabel: 'Time Period',
            };
          }

          // Always update state and close loading
          set(state => ({
            cardMetrics: metricData
              ? { ...state.cardMetrics, [cardId]: metricData }
              : state.cardMetrics,
            cardLoadingStates: {
              ...state.cardLoadingStates,
              [cardId]: false,
            },
          }));
        } catch (error) {
          console.error(`Failed to fetch metric for card ${cardId}:`, error);
          set(state => ({
            cardLoadingStates: {
              ...state.cardLoadingStates,
              [cardId]: false,
            },
            error:
              error instanceof Error ? error.message : 'Failed to load support performance data',
          }));
        }
      },

      setCardTimeRange: (cardId, timeRange) => {
        const currentCardTimeRange = get().cardTimeRanges[cardId];
        // Only update if the time range actually changed
        if (currentCardTimeRange !== timeRange) {
          set(state => ({
            cardTimeRanges: {
              ...state.cardTimeRanges,
              [cardId]: timeRange,
            },
          }));
          // Don't fetch here - let individual cards handle their own data
        }
      },

      setError: error => set({ error }),

      // Reset store state (for user switch)
      reset: () => {
        set({
          metrics: [],
          avgTimeFirstResponse: null,
      humanHandlingTime: null,
          questionCount: null,
          engagementTime: null,
          todayConversations: null,
          isLoading: false,
          error: null,
          dateRange: getDefaultDateRange(),
          selectedPeriod: 'last7Days',
          selectedTimeRange: '12months',
          cardTimeRanges: {},
          cardMetrics: {},
          cardLoadingStates: {},
        });
      },

      // Transform real API data to legacy metrics format
      transformToLegacyMetrics: () => {
        const state = get();
        const { avgTimeFirstResponse, questionCount, engagementTime, humanHandlingTime, dateRange } =
          state;

        if (!avgTimeFirstResponse || !questionCount || !engagementTime) {
          return;
        }

        const metrics: MetricData[] = [
          // Card 1: Average resolution time (purple - bar chart comparison)
          {
            title: 'Average resolution time',
            value: SupportPerformanceService.formatTimeValue(
              avgTimeFirstResponse.avgTimeFirstResponse
            ),
            subtitle: 'Average resolution time',
            change: avgTimeFirstResponse.trend || 0,
            chartData: transformTimeSeriesData(avgTimeFirstResponse.timeSeriesData, dateRange),
            chartColor: '#6B33FF',
            yAxisLabel: 'Response Time',
            xAxisLabel: 'Time Period',
            rawValue: avgTimeFirstResponse.avgTimeFirstResponse, // Raw value in seconds for comparison chart
            // 样本为 0 时留 undefined，让那根对照柱子消失而不是显示 0 秒
            humanValue:
              humanHandlingTime && humanHandlingTime.recordCount > 0
                ? humanHandlingTime.avgSeconds
                : undefined,
          },
          // Card 2: Resolved Conversations (green line)
          {
            title: 'Resolved Conversations',
            value: questionCount.totalResolvedConversations.toLocaleString(),
            subtitle: 'Total resolved conversations',
            change: questionCount.resolvedTrend || 0,
            chartData: transformTimeSeriesData(questionCount.resolvedConversationsData, dateRange),
            chartColor: '#00BC2F',
            yAxisLabel: 'Conversations',
            xAxisLabel: 'Time Period',
          },
          // Card 3: Resolution Rate (yellow line)
          {
            title: 'Resolution Rate',
            value: `${questionCount.averageResolutionRate.toFixed(1)}%`,
            subtitle: 'Percent of resolved conversations',
            change: questionCount.rateTrend || 0,
            chartData: transformTimeSeriesData(questionCount.resolutionRateData, dateRange),
            chartColor: '#FBD300',
            yAxisLabel: 'Resolution Rate (%)',
            xAxisLabel: 'Time Period',
          },
          // Card 4: Average engagement time (green line)
          {
            title: 'Average engagement time',
            value: SupportPerformanceService.formatTimeValue(engagementTime.averageEngagementTime),
            subtitle:
              'Total duration a user actively interacts with the chatbot including typing, clicking, or reading responses.',
            change: engagementTime.trend || 0,
            chartData: transformTimeSeriesData(engagementTime.timeSeriesData, dateRange),
            chartColor: '#00BC2F',
            yAxisLabel: 'Sessions',
            xAxisLabel: 'Time Period',
          },
          // Card 5: AI Generated Orders (pink line) - empty data, no API yet
          {
            title: 'AI Generated Orders',
            value: '0',
            subtitle: 'Percent of resolved conversations',
            chartData: {
              labels: generateEmptyStateLabels(dateRange),
              data: generateEmptyStateLabels(dateRange).map(() => 0),
              recordCounts: [],
            },
            chartColor: '#FF41B3',
            yAxisLabel: 'Orders',
            xAxisLabel: 'Time Period',
          },
          // Card 6: Handled by Humans (red line) - empty data, no API yet
          {
            title: 'Handled by Humans',
            value: '',
            showViewReport: true,
            chartData: {
              labels: generateEmptyStateLabels(dateRange),
              data: generateEmptyStateLabels(dateRange).map(() => 0),
              recordCounts: [],
            },
            chartColor: '#FF5E57',
            yAxisLabel: 'Sessions',
            xAxisLabel: 'Time Period',
          },
        ];

        set({ metrics });
      },
    }),
    {
      name: 'support-performance-storage',
      partialize: state => ({
        dateRange: state.dateRange,
        selectedPeriod: state.selectedPeriod,
      }),
    }
  )
);
